/**
 * The one shared source of truth for in-app help content (`BACKLOG.md` B6).
 *
 * Every place help text appears — the full `/help` page, an inline `HelpLink` disclosure next to a
 * control, and the grounding context the `B6a` chat agent quotes from — reads from `HELP_TOPICS`
 * below. Nothing renders its own copy of an explanation. Two copies of the same explanation drift
 * the moment one is edited and the other is not (`claude-kit/rules/invariants.md`'s in-app-help
 * section, "nothing is written twice"), and for the chat agent specifically, a second copy would
 * also be a second place the compliance boundary (never ground the agent in anything but this
 * static content plus a route name) could be quietly widened.
 *
 * Lives in `shared/` rather than `web/` — unlike `web/src/routes/AccessPage.tsx`'s
 * `CAPABILITY_COPY`, which is presentation copy for a decision `shared/` already owns — because
 * this content itself has two independent consumers on two independent sides of the workspace
 * boundary (`web` renders it, `server` grounds an LLM call in it), and `@emr/shared` is the one
 * package both already depend on. `shared/src/authorization.ts`'s reasoning for keeping the
 * *decision* there and the *gloss* in `web/` does not apply here: there is no decision this module
 * makes, only prose neither side may fork.
 *
 * **Ordered by real user confusion, not the feature list** — `claude-kit/rules/invariants.md`'s
 * explicit instruction for this shape, following `coc`'s own `help.ts`. Each topic's own comment
 * says what confusion it exists to resolve. `HELP_TOPICS`' array order is also `/help`'s own
 * section order for the same reason: the first thing a person new to this app gets stuck on is
 * signing in, not scheduling.
 *
 * **Covers what exists in this build today, honestly, including the gaps.** `patients-and-
 * scheduling` below is the deliberate example: this web app has no patient-chart or scheduling
 * screens yet (only the server-side API surface does), and the topic says so rather than
 * describing screens that do not exist — matching `invariants.md`'s "an empty or degenerate state
 * gets explained too, not only the happy path".
 */

export interface HelpTopic {
  /** Stable, kebab-case, deep-linkable anchor. Renamed only with real cost — see `web/src/routes/
   * HelpPage.tsx`'s stale-link fallback, which is the safety net for a rename anyway. */
  readonly id: string;
  readonly title: string;
  /** One sentence. Used as the inline `HelpLink` disclosure's short answer, and as the compact
   * per-topic summary handed to the chat agent as grounding context. */
  readonly summary: string;
  /** The full explanation, as separate paragraphs — `HelpPage.tsx` renders one `<p>` per entry
   * rather than one block of prose, matching every other multi-paragraph panel in this app. */
  readonly body: readonly string[];
  /** Free-text terms `topicMatcher.ts` matches a chat question against, in addition to words
   * already in `title`. Lowercase; matching is case-insensitive regardless. */
  readonly keywords: readonly string[];
}

export const HELP_TOPICS: readonly HelpTopic[] = [
  // The first thing anyone gets stuck on: getting in, and getting kicked back out unexpectedly.
  {
    id: 'signing-in',
    title: 'Signing in, and staying signed in',
    summary:
      'You sign in with your practice email and password; a session ends on its own after a while, and that is expected, not a bug.',
    body: [
      'Sign in with the email and password your practice administrator set up for you. There is no self-service "forgot password" link on this screen yet — if you are locked out, ask a practice administrator to help, rather than trying to guess.',
      'A session does not last forever. It ends after a period of inactivity, and separately it has a hard expiration regardless of activity — whichever comes first. When either happens, the next thing you try to do sends you back to this page. That is the system working correctly, not losing your work; anything you had already saved is still saved.',
      'The "Sign out" button in the top-right of the header ends your session immediately, rather than waiting for it to expire on its own. Use it before walking away from a shared or public computer.',
      'If you see a colored banner across the very top of the page, it is telling you which environment you are in. A demo or test environment behaves the same as the real thing but never holds real patient data — the banner is how you can tell which one you are looking at.',
    ],
    keywords: [
      'sign in',
      'signin',
      'log in',
      'login',
      'password',
      'session',
      'sign out',
      'signout',
      'logged out',
      'signed out',
      'timeout',
      'timed out',
      'expired',
      'expire',
      'locked out',
      'banner',
      'environment',
    ],
  },

  // Second most common question in an RBAC-gated app: "why can't I see X", and "what does my role
  // actually let me do".
  {
    id: 'your-access',
    title: 'What your role lets you do',
    summary:
      'The "Your access" page lists every capability your role actually grants — it is the real answer, not a guess about what your job title should include.',
    body: [
      'Open "Your access" from the navigation to see the exact list of capabilities your role grants in this practice — reading patient charts, writing notes, signing your own notes versus co-signing someone else\'s, managing the schedule, and so on. Each row also says whether it only reads data or actually changes something.',
      "Holding a capability is not the same as being able to open one particular patient's chart. Two more checks always apply on top of it: whether you have a real care-team relationship to that specific patient, and whether the record carries a confidentiality restriction you are not permitted to see. A capability can be present on your access page and a specific chart can still correctly refuse you — that is two different systems doing two different jobs, not a contradiction.",
      'If a navigation item or a whole page is simply missing rather than showing an error, that almost always means your role does not include the capability it needs — the app hides what is not part of your job rather than showing it disabled with an explanation on every page load. A practice administrator can change your role if it should include more; this is not something the plan or subscription tier affects.',
    ],
    keywords: [
      'access',
      'permission',
      'permissions',
      'capability',
      'capabilities',
      'role',
      'cannot see',
      "can't see",
      'missing',
      'hidden',
      'forbidden',
      'not authorized',
      'authorised',
      'authorized',
      'why cant i',
    ],
  },

  // Third: the upgrade-vs-permission distinction is genuinely confusing and this app deliberately
  // renders it as two differently-worded refusals — worth explaining once, in one place.
  {
    id: 'plans-and-upgrades',
    title: '"Ask an admin" versus "upgrade the plan" — why the wording differs',
    summary:
      'A page refuses you one of two different ways on purpose: a role gap needs a different role, a plan gap needs a different plan, and the wording tells you which.',
    body: [
      'Some pages are gated by what your role permits (a permission question) and some are gated by what your practice has paid for (an entitlement question) — a few, like Analytics, can be gated by both at once. This app deliberately words the two refusals differently rather than showing one generic "access denied" message, because the fix for each one is different.',
      'If a page tells you your role does not hold a needed capability, upgrading the plan will not fix it — a practice administrator has to change your role. If a page tells you a feature is not included on the current plan, a role change will not fix it — a practice administrator has to change the plan. Being told the wrong one of these would send someone chasing a fix that does nothing.',
      'A capability gap is checked before a plan gap: if your role would never be allowed to use a feature at all, you will not see an "upgrade" prompt for it, since offering to sell a practice something a given role could never use anyway would be misleading.',
    ],
    keywords: [
      'upgrade',
      'plan',
      'tier',
      'entitlement',
      'entitled',
      'subscription',
      'billing plan',
      'not included',
      'admin',
      'administrator',
      'why cant i',
      "why can't i",
    ],
  },

  {
    id: 'audit-log',
    title: 'The audit log page',
    summary:
      'The Audit log page shows this practice’s login and logout history — who signed in or out, when, and from where. It does not yet show every chart read or write; that broader view is still being built.',
    body: [
      'The "Audit log" navigation item only appears for roles that hold the audit:read capability — typically a practice administrator or a dedicated auditor role, and no clinical role by default. If you cannot see it, that is the permission system working as intended; see "What your role lets you do" for how to check.',
      'What is here today is login/logout only: who signed in or out, when, whether it succeeded, and the source IP/device it came from. A row marked "Denied" is real and often the more interesting signal — for example, two people racing to use the same one-time MFA code, where the loser is correctly refused and recorded.',
      'A failed login attempt made before any practice was chosen (a wrong password, before the system even knows which of your practices to check) is intentionally not shown here — it has no practice to scope it to yet, so it is recorded separately rather than appearing to belong to this one.',
      'What is not here yet: a view of every chart read and write across the whole practice (who looked at which patient’s record, and when). The underlying tamper-evident audit trail already records that, in full, on every clinical page — this page just does not read it back out yet. That is a known, tracked gap in this build, not a sign that nothing is being audited.',
    ],
    keywords: [
      'audit',
      'audit log',
      'login log',
      'logout log',
      'sign in history',
      'who logged in',
      'empty',
      'blank',
      'nothing shows',
      'access log',
      'who read',
      'compliance',
    ],
  },

  // Deliberately honest about an empty/degenerate state, per invariants.md's own instruction —
  // this app's clinical and scheduling capabilities are real on the server but have no screens yet.
  {
    id: 'patients-and-scheduling',
    title: 'Where are my patients, and my schedule?',
    summary:
      'There is no patient list or calendar screen in this build yet — the underlying capabilities exist, but nothing in the navigation opens them.',
    body: [
      'If you came looking for a patient list, a chart, or an appointment calendar in the navigation, you will not find one yet — this build does not have those screens built. This is a real, known gap in progress, not something misconfigured on your account.',
      'The capabilities that will eventually govern those screens already exist, and you can see them on "Your access" today: reading and writing patient demographics, reading and writing clinical chart content, prescribing, signing and co-signing notes, and managing the schedule. A role can already hold every one of those capabilities with nothing yet in the app for it to unlock.',
      'If you are testing or demonstrating this system, treat this as expected for now. There is no synthetic patient data seeded into a working chart experience either — this is not a case of data existing that the UI merely fails to show.',
    ],
    keywords: [
      'patient',
      'patients',
      'chart',
      'schedule',
      'scheduling',
      'appointment',
      'appointments',
      'calendar',
      'where is',
      "where's",
      'missing page',
      'not found',
    ],
  },

  // Secure messaging (`BACKLOG.md` W2, pillar 3) — the first real cross-patient work queue in the
  // nav, and its two thread kinds (a care-team relationship vs. a shared unassigned pool) behave
  // differently enough from every other list in this app that staff genuinely get stuck on "why
  // can't I claim this one" without an explanation.
  {
    id: 'secure-messaging',
    title: 'The inbox: care-team threads vs. the general queue',
    summary:
      'A care-team thread is visible to anyone with an active relationship to that patient and has no single owner; a general thread sits in one shared unassigned pool until a staff member claims it.',
    body: [
      'The inbox has two different kinds of thread, and they behave differently on purpose. A "care team" thread is visible to every staff member with an active care-team relationship to that patient — the attending, and anyone covering or consulting — and any of them may reply. There is no owner to claim, so you will not see a "Claim thread" button on one.',
      'A "general" thread is different: it starts in one shared, practice-wide unassigned pool, and any staff member holding patient-record access can claim it with the "Claim thread" button. Claiming is first-come — if someone else claims it a moment before you, you will see that the thread is already taken rather than being handed to you anyway. The category shown on a general thread (billing, scheduling, a general question) is there to help you decide what it is about; it does not route the thread to anyone automatically.',
      'Closing a thread works the same way for both kinds. If a patient replies after you close it, the thread reopens on its own — and if it was a general thread you had claimed, it comes back to you rather than back into the unassigned pool.',
      'The number next to "Inbox" in the navigation is a count of unclaimed general threads, not a count of every unread message — claiming one, or nobody claiming one, is the situation that count exists to catch, since staff do not get an email of their own for a new message the way a patient does for a reply.',
    ],
    keywords: [
      'inbox',
      'message',
      'messages',
      'messaging',
      'thread',
      'threads',
      'claim',
      'unassigned',
      'care team',
      'general queue',
      'unread',
      'badge',
    ],
  },

  {
    id: 'overview-page',
    title: 'What the Overview page is telling you',
    summary:
      "Overview shows exactly what your current session actually is: who you are, which practice, your role, and the practice's plan.",
    body: [
      'The Overview page — the first thing you see after signing in — is a plain readout of your current session: your email, the practice you are signed into, your role in it, and whether you are a platform operator rather than an ordinary practice member.',
      'Its "Plan" panel shows the practice\'s subscription tier, whether that tier includes certified EHR technology (relevant to MIPS attestation), and exactly which certified capabilities the tier includes. None of this is editable from here — it reflects what is actually configured for the practice, the same facts "Ask an admin versus upgrade the plan" explains the consequences of.',
    ],
    keywords: [
      'overview',
      'home page',
      'landing page',
      'dashboard',
      'session info',
      'tier',
      'certified',
      'certification',
      'cehrt',
    ],
  },
  // Personal profile settings — lower-urgency than getting stuck signing in or reading a chart, so
  // it sits near the end, matching this file's own confusion-ordered convention.
  {
    id: 'your-signature',
    title: 'Your signature',
    summary:
      'Draw a mark or adopt one of the bundled fonts — this is what appears on every clinical note you sign, alongside the credential line a practice administrator sets for you.',
    body: [
      'Your signature mark is what gets attached to a note when you sign it. You can either draw it by hand or adopt one of the bundled fonts to render your name — switching between the two at any time replaces whichever mark was there before.',
      'The "Signature line" beneath your mark — your credentials and title, e.g. "MD, Family Medicine" — is not something you set yourself. A practice administrator sets it for every member, including you, from their own "Colleague signature lines" panel. If it reads "Not set," ask an administrator to fill it in rather than looking for a control on this page that is not there.',
      'Your profile photo lives on this same page, for the same reason: it is shown next to your name throughout the practice, not attached to any patient chart, and is not treated as clinical content.',
    ],
    keywords: [
      'signature',
      'sign a note',
      'signature mark',
      'signature line',
      'credentials',
      'draw signature',
      'profile photo',
    ],
  },
] as const;

/** Look up one topic by its stable id. `null` for an id that does not (or no longer) match any
 * topic — the caller decides what "not found" means (`HelpPage.tsx`'s stale-link fallback,
 * `topicMatcher.ts`'s grounding lookup). */
export function getHelpTopic(id: string): HelpTopic | null {
  return HELP_TOPICS.find((topic) => topic.id === id) ?? null;
}
